import { useState, useMemo, useCallback } from "react";
import { format, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import SortData from "../utils/SortData";
import { NotificationManager } from "../scripts/functions/NotificationManager";
import { useWeeklyRoles, useSaveWeeklyRoles } from "../scripts/queries/useWeeklyRolesQueries";

// Avoid a new object render when query data is undefined
const EMPTY_DATA = {roles: [], opciones: {turnos: [], puestos: [], maquinas: [], ausencias: []}};

const getMonday = (weekOffset) => {
    const monday = new Date();
    monday.setHours(0, 0, 0, 0);
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7) + weekOffset * 7);
    return monday;
}

//The current week applies from today by default, the next one from its Monday
const getDefaultFromDate = (weekOffset) => format(weekOffset === 0 ? new Date() : getMonday(weekOffset), 'yyyy-MM-dd');

export const useWeeklyRolesNormalizer = () => {
    const [weekOffset, setWeekOffset] = useState(0);   //0 = current week, 1 = next week
    const [edits, setEdits] = useState({});            //{nomina: {turno, maquina, puesto}}, pending until saved
    const [search, setSearch] = useState("");
    const [onlyIncomplete, setOnlyIncomplete] = useState(false);
    const [fromDate, setFromDate] = useState(getDefaultFromDate(0));

    const monday = useMemo(() => getMonday(weekOffset), [weekOffset]);
    const weekStart = format(monday, 'yyyy-MM-dd');

    const {data = EMPTY_DATA, refetch: fetchRoles, isLoading: loading, isFetching} = useWeeklyRoles(weekStart);
    const {mutate: saveRoles, isPending: isSaving} = useSaveWeeklyRoles();
    const {roles, opciones} = data;

    const turnosMap = useMemo(() => Object.fromEntries(opciones.turnos.map(t => [t.turno, t])), [opciones]);

    //Horario and días always come from the turno
    const buildRow = useCallback((role, edit) => {
        const merged = {...role, ...edit};
        const turno = turnosMap[merged.turno];
        const isAbsent = opciones.ausencias.includes(merged.maquina);
        //Cells that block saving: empty or outside the lists (absences don't need turno or puesto)
        const errores = {
            turno: !isAbsent && !merged.turno,
            maquina: !opciones.maquinas.includes(merged.maquina),
            puesto: !isAbsent && !opciones.puestos.includes(merged.puesto),
        };

        return {...merged, horario: turno?.horario || '', dias: turno?.dias || '', editado: !!edit, errores, incompleto: Object.values(errores).some(Boolean)};
    }, [turnosMap, opciones]);

    //Rows without edits keep the same object, so the table only redraws the rows that changed
    const savedRows = useMemo(() => roles.map(role => buildRow(role)), [roles, buildRow]);
    const rows = useMemo(() => savedRows.map((row, i) => edits[row.nomina] ? buildRow(roles[i], edits[row.nomina]) : row), [savedRows, roles, edits, buildRow]);

    const filteredRows = useMemo(() => {
        const term = search.toLowerCase();
        return rows.filter(row => (!onlyIncomplete || row.incompleto) && (!term
            || String(row.nomina).includes(term)
            || (row.nombre || '').toLowerCase().includes(term)
            || (row.maquina || '').toLowerCase().includes(term)));
    }, [rows, search, onlyIncomplete]);

    const incompleteCount = useMemo(() => rows.filter(row => row.incompleto).length, [rows]);

    const {sortedItems, requestSort, getIcon} = SortData(filteredRows);

    //Changes one cell; the edit is dropped if the row went back to its saved values.
    const updateField = useCallback((nomina, field, value) => {
        const original = roles.find(r => r.nomina === nomina);
        setEdits(prev => {
            const values = {turno: original.turno, maquina: original.maquina, puesto: original.puesto, ...prev[nomina], [field]: value === '' ? null : value};
            const isUnchanged = ['turno', 'maquina', 'puesto'].every(key => (original[key] ?? '') === (values[key] ?? ''));
            const {[nomina]: _, ...rest} = prev;
            return isUnchanged ? rest : {...rest, [nomina]: values};
        });
    }, [roles]);

    const pendingCount = Object.keys(edits).length;

    const changeWeek = (offset) => {
        if (offset === weekOffset) return;
        if (pendingCount > 0) {
            NotificationManager.push("Guarda o descarta los cambios antes de cambiar de semana", false);
            return;
        }
        setWeekOffset(offset);
        setFromDate(getDefaultFromDate(offset));
    }

    const discardEdits = () => setEdits({});

    //Days of the selected week the changes can apply from
    const dayOptions = useMemo(() => Array.from({length: 7}, (_, i) => {
        const day = addDays(monday, i);
        return {value: format(day, 'yyyy-MM-dd'), label: format(day, "EEEE dd/MM", {locale: es})};
    }), [monday]);

    //The whole table is sent: on a Monday save, employees missing from it lose their default
    const save = () => {
        if (incompleteCount > 0) {
            NotificationManager.push(`Completa turno, máquina y puesto de ${incompleteCount} empleado(s) antes de guardar`, false);
            return;
        }
        const payload = {weekStart, fromDate, roles: rows.map(({nomina, turno, maquina, puesto}) => ({nomina, turno, maquina, puesto}))};
        saveRoles(payload, {
            onSuccess: (res) => {
                setEdits({});
                if (res?.warnings?.length) {
                    console.warn("Advertencias al guardar los roles: ", res.warnings);
                    NotificationManager.push(`Guardado con ${res.warnings.length} advertencia(s): ${res.warnings.slice(0, 3).join('; ')}`, false);
                }
            }
        });
    }

    return {rows: sortedItems, opciones, weekOffset, monday, search, pendingCount, incompleteCount, onlyIncomplete, loading, isFetching, isSaving, dayOptions, fromDate,
        fetchRoles, requestSort, getIcon, updateField, changeWeek, discardEdits, setSearch, setOnlyIncomplete, setFromDate, save};
}